import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './Navbar.module.css';
import Icon from '../Icon/Icon';
import LoginModal from '../LoginModal/LoginModal';
import RegisterModal from '../RegisterModal/RegisterModal';
import { useAuth } from '../../hooks/useAuth';
// Importar animaciones globales para que los @keyframes estén disponibles
import '../../styles/animations.module.css';

/**
 * Navbar Component - TECNILAND Navigation
 * DESKTOP-FIRST: Animaciones optimizadas para 1920px+
 * MOBILE-OPTIMIZED: Hamburger menu con performance mejorada
 * Con sistema de autenticación integrado
 */
function Navbar() {
  const [activeSection, setActiveSection] = useState('hero');
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Estado de autenticación
  const { isAuthenticated, user, loading } = useAuth();
  
  // Estado de modales
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Detectar scroll para efectos de navbar (optimizado con useCallback)
  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Bloquear scroll del body cuando menú hamburguesa está abierto en móvil
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Links de navegación - todos con scroll anchor excepto Server
  const links = useMemo(() => [
    { id: 'hero', label: 'Inicio', icon: 'home', anchor: '#hero' },
    { id: 'features', label: 'Características', icon: 'features', anchor: '#features' },
    { id: 'modpacks', label: 'Modpacks', icon: 'modpacks', anchor: '#modpacks' },
    { id: 'lore', label: 'Lore', icon: 'lore', anchor: '#lore' },
    { id: 'roadmap', label: 'Roadmap', icon: 'roadmap', anchor: '#roadmap' },
    { id: 'gallery', label: 'Galería', icon: 'gallery', anchor: '#gallery' },
    { id: 'server', label: 'Servidor', icon: 'server', path: '/server', external: true },
  ], []);

  // Detectar sección activa con Intersection Observer
  useEffect(() => {
    if (location.pathname !== '/') return;

    const observerOptions = {
      root: null,
      rootMargin: '-50% 0px -50% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observar todas las secciones
    links.forEach((link) => {
      if (!link.external && link.anchor) {
        const element = document.querySelector(link.anchor);
        if (element) observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [location.pathname, links]);

  // Smooth scroll a sección (memoizado para performance)
  const scrollToSection = useCallback((anchor) => {
    const element = document.querySelector(anchor);
    if (element) {
      const navbarHeight = 80; // Altura del navbar
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Ref para limpiar timer de navegación
  const navigationTimerRef = React.useRef(null);

  // Cleanup de timer al desmontar
  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
      }
    };
  }, []);

  // Manejar click en links (memoizado)
  const handleLinkClick = useCallback((link) => {
    // Limpiar timer anterior si existe
    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
    }
    
    // Cerrar menú móvil inmediatamente para feedback instantáneo
    setIsOpen(false);

    if (link.external && link.path) {
      // Server es ruta externa
      navigate(link.path);
    } else if (link.anchor) {
      // Pequeño delay para permitir que el menú se cierre antes del scroll
      navigationTimerRef.current = setTimeout(() => {
        // Si estamos en otra página, primero ir a home
        if (location.pathname !== '/') {
          navigate('/');
          // NO hacer scroll automático - ScrollToTop se encargará de ir al inicio
        } else {
          scrollToSection(link.anchor);
        }
        setActiveSection(link.id);
      }, 100); // Dar tiempo para que se cierre el menú en móvil
    }
  }, [navigate, location.pathname, scrollToSection]);

  // Determinar link activo (memoizado)
  const isLinkActive = useCallback((link) => {
    if (link.external && link.path) {
      return location.pathname === link.path;
    }
    return location.pathname === '/' && activeSection === link.id;
  }, [location.pathname, activeSection]);

  // Abrir modal login
  const openLoginModal = useCallback(() => {
    setIsOpen(false);
    setShowRegisterModal(false);
    setShowLoginModal(true);
  }, []);

  // Abrir modal registro
  const openRegisterModal = useCallback(() => {
    setIsOpen(false);
    setShowLoginModal(false);
    setShowRegisterModal(true);
  }, []);

  // Escuchar eventos globales para abrir modales desde otros componentes
  useEffect(() => {
    const handleOpenLogin = () => openLoginModal();
    const handleOpenRegister = () => openRegisterModal();

    window.addEventListener('openLoginModal', handleOpenLogin);
    window.addEventListener('openRegisterModal', handleOpenRegister);

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLogin);
      window.removeEventListener('openRegisterModal', handleOpenRegister);
    };
  }, [openLoginModal, openRegisterModal]);

  // Cerrar modales
  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  const closeRegisterModal = useCallback(() => {
    setShowRegisterModal(false);
  }, []);

  // Switch entre modales
  const switchToRegister = useCallback(() => {
    setShowLoginModal(false);
    setShowRegisterModal(true);
  }, []);

  const switchToLogin = useCallback(() => {
    setShowRegisterModal(false);
    setShowLoginModal(true);
  }, []);

  return (
    <>
      <nav className={`${styles.navbar} ${scrolled ? styles.navbarScrolled : ''}`}>
        <div className={styles.navbarContainer}>
          {/* Logo */}
          <button 
            className={styles.navbarLogo} 
            onClick={() => handleLinkClick({ id: 'hero', anchor: '#hero' })}
          >
            <span className={styles.logoText} translate="no">TECNILAND</span>
            <span className={styles.logoIcon}>◆</span>
          </button>

          {/* Menu Toggle (Mobile) */}
          <button
            className={`${styles.menuToggle} ${isOpen ? styles.menuToggleOpen : ''}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            aria-expanded={isOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* Menu Links */}
          <ul className={`${styles.navbarMenu} ${isOpen ? styles.navbarMenuOpen : ''}`}>
            {links.map((link) => (
              <li key={link.id} className={styles.menuItem}>
                <button
                  className={`${styles.menuLink} ${isLinkActive(link) ? styles.menuLinkActive : ''}`}
                  onClick={() => handleLinkClick(link)}
                >
                  <span className={styles.linkEmoji}>
                    <Icon name={link.icon} size="sm" animation="float" />
                  </span>
                  <span className={styles.linkText}>{link.label}</span>
                </button>
              </li>
            ))}
            
            {/* Auth buttons en menú móvil */}
            <li className={`${styles.menuItem} ${styles.mobileAuthItem}`}>
              {!loading && (
                isAuthenticated ? (
                  <button 
                    className={styles.mobileAccountBtn}
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/dashboard');
                    }}
                  >
                    <Icon name="user" size="sm" />
                    <span>Hola, {user?.username}</span>
                  </button>
                ) : (
                  <div className={styles.mobileAuthButtons}>
                    <button 
                      className={styles.mobileLoginBtn}
                      onClick={openLoginModal}
                    >
                      <Icon name="login" size="sm" />
                      <span>Inicia Sesión</span>
                    </button>
                    <button 
                      className={styles.mobileRegisterBtn}
                      onClick={openRegisterModal}
                    >
                      <Icon name="register" size="sm" />
                      <span>Crear Cuenta</span>
                    </button>
                  </div>
                )
              )}
            </li>
          </ul>

          {/* Auth Buttons - Desktop */}
          <div className={styles.authButtons}>
            {loading ? (
              <div className={styles.authLoading}>
                <span className={styles.loadingSpinner}></span>
              </div>
            ) : isAuthenticated ? (
              <button 
                className={styles.accountBtn}
                onClick={() => navigate('/dashboard')}
              >
                <Icon name="user" size="sm" />
                <span className={styles.accountBtnText} data-hover="Ver cuenta">
                  Hola, {user?.username}
                </span>
              </button>
            ) : (
              <>
                <button 
                  className={styles.loginBtn}
                  onClick={openLoginModal}
                >
                  <Icon name="login" size="sm" />
                  <span>Inicia Sesión</span>
                </button>
                <button 
                  className={styles.registerBtn}
                  onClick={openRegisterModal}
                >
                  <Icon name="register" size="sm" />
                  <span>Crear Cuenta</span>
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Modales de autenticación */}
      <LoginModal 
        isOpen={showLoginModal}
        onClose={closeLoginModal}
        onSwitchToRegister={switchToRegister}
      />
      <RegisterModal 
        isOpen={showRegisterModal}
        onClose={closeRegisterModal}
        onSwitchToLogin={switchToLogin}
      />
    </>
  );
}

export default Navbar;
